import { action, makeObservable, observable, runInAction } from 'mobx';
import { SetPreferencesProperties, WorkflowPreferences as KnockWorkflowPreferences } from '@knocklabs/client';

import Logger from 'utils/logger';

import { authenticateKnockClient, getKnockClient } from 'utils/knock/knockClient';

import RootStore from 'stores/Root';

import {
  CHANNEL_TYPES,
  DERIVED_WORKFLOW_KEYS,
  getItemChannels,
  getWorkflowDefault,
  NOTIFICATION_CATEGORIES,
  WorkflowKey,
  XOR_PAIRS,
} from './config';
import { WorkflowPreferences } from './types';

class KnockPreferencesStore {
  store: RootStore;

  @observable isLoading = false;

  @observable isLoaded = false;

  @observable isFailure = false;

  @observable isDisabled = false;

  @observable isSaving = false;

  @observable preferences: WorkflowPreferences = {};

  @observable unknownWorkflows: KnockWorkflowPreferences = {};

  // Knock category prefs we don't model in NOTIFICATION_CATEGORIES — preserved across saves
  // because setPreferences replaces the whole set (mirrors unknownWorkflows).
  @observable unknownCategories: KnockWorkflowPreferences = {};

  constructor(rootStore: RootStore) {
    makeObservable(this);
    this.store = rootStore;
  }

  @action.bound
  async loadPreferences(): Promise<void> {
    if (this.isLoading) return;

    try {
      this.isLoading = true;
      const enabled = await authenticateKnockClient();
      if (!enabled) {
        runInAction(() => {
          this.isDisabled = true;
          this.isLoaded = true;
          this.isFailure = false;
        });
        return;
      }
      const client = getKnockClient();
      const preferenceSet = await client.user.getPreferences();
      const role = this.store.session.user?.role;

      const prefs: WorkflowPreferences = {};
      const knownWorkflowKeys = new Set<string>();
      const knownCategoryKeys = new Set<string>();
      for (const category of NOTIFICATION_CATEGORIES) {
        for (const item of category.workflows) {
          const isCategory = item.kind === 'category';
          (isCategory ? knownCategoryKeys : knownWorkflowKeys).add(item.key);
          // Category-typed rows read from preferenceSet.categories; everything else from .workflows.
          const source = isCategory ? preferenceSet.categories : preferenceSet.workflows;
          prefs[item.key] = {};
          const itemDefault = getWorkflowDefault(item.key, role);
          for (const channel of getItemChannels(item)) {
            const pref = source?.[item.key];
            if (pref && typeof pref === 'object' && 'channel_types' in pref) {
              const channelTypes = (pref as { channel_types?: Record<string, boolean> }).channel_types;
              prefs[item.key][channel.key] = channelTypes?.[channel.key] ?? itemDefault;
            } else {
              prefs[item.key][channel.key] = itemDefault;
            }
          }
        }
      }

      const derivedKeySet = new Set<string>(DERIVED_WORKFLOW_KEYS);
      const unknown: KnockWorkflowPreferences = {};
      for (const [key, value] of Object.entries(preferenceSet.workflows ?? {})) {
        if (!knownWorkflowKeys.has(key) && !derivedKeySet.has(key)) unknown[key] = value;
      }
      const unknownCats: KnockWorkflowPreferences = {};
      for (const [key, value] of Object.entries(preferenceSet.categories ?? {})) {
        if (!knownCategoryKeys.has(key)) unknownCats[key] = value;
      }

      runInAction(() => {
        this.preferences = prefs;
        this.unknownWorkflows = unknown;
        this.unknownCategories = unknownCats;
        this.isLoaded = true;
        this.isFailure = false;
      });
    } catch (e) {
      runInAction(() => {
        this.isFailure = true;
        this.isLoaded = false;
      });
      Logger.error('Failed to load Knock preferences', e);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  private buildPreferencesPayload(): SetPreferencesProperties {
    const role = this.store.session.user?.role;
    const workflows: KnockWorkflowPreferences = { ...this.unknownWorkflows };
    const categories: KnockWorkflowPreferences = { ...this.unknownCategories };
    for (const category of NOTIFICATION_CATEGORIES) {
      for (const item of category.workflows) {
        const channelTypes: Record<string, boolean> = {};
        const itemDefault = getWorkflowDefault(item.key, role);
        for (const ch of getItemChannels(item)) {
          channelTypes[ch.key] = this.preferences[item.key]?.[ch.key] ?? itemDefault;
        }
        const target = item.kind === 'category' ? categories : workflows;
        target[item.key] = { channel_types: channelTypes };
      }
    }
    // Defense-in-depth XOR normalization: if both halves of a pair end up ON via drift, force the
    // partner OFF. Last-write-wins so saves can't violate the invariant the backend depends on.
    const channelsFor = (key: string): Record<string, boolean> | undefined => {
      const entry = workflows[key];
      return typeof entry === 'object' && entry !== null && 'channel_types' in entry
        ? (entry.channel_types as Record<string, boolean>)
        : undefined;
    };
    for (const [key, partnerKey] of Object.entries(XOR_PAIRS)) {
      const keyChannels = channelsFor(key);
      const partnerChannels = channelsFor(partnerKey);
      if (!keyChannels || !partnerChannels) continue;
      for (const ch of CHANNEL_TYPES) {
        if (keyChannels[ch.key] && partnerChannels[ch.key]) {
          partnerChannels[ch.key] = false;
        }
      }
    }
    // Derived workflow: ON iff Added-as-Collab ON, broadcast OFF, collab-only OFF (state 001).
    const newEvChannels = channelsFor(WorkflowKey.eventReported);
    const collabOnlyChannels = channelsFor(WorkflowKey.eventReportedCollaboratorOnly);
    const derivedChannels: Record<string, boolean> = {};
    for (const ch of CHANNEL_TYPES) {
      const addedColl =
        this.preferences[WorkflowKey.eventCollaborated]?.[ch.key] ?? getWorkflowDefault(WorkflowKey.eventCollaborated);
      const newEv = newEvChannels?.[ch.key] ?? getWorkflowDefault(WorkflowKey.eventReported);
      const collabOnly = collabOnlyChannels?.[ch.key] ?? getWorkflowDefault(WorkflowKey.eventReportedCollaboratorOnly);
      derivedChannels[ch.key] = Boolean(addedColl) && !newEv && !collabOnly;
    }
    workflows[WorkflowKey.eventCollaboratedFreshWindow] = { channel_types: derivedChannels };
    return { workflows, categories };
  }

  @action.bound
  async updateWorkflowChannel(workflowKey: string, channel: string, enabled: boolean): Promise<void> {
    if (!this.isLoaded) return;

    // XOR pair: turning one ON forces the other OFF. Both being OFF is allowed.
    const xorPartner = enabled ? XOR_PAIRS[workflowKey] : undefined;

    const previousValues: Record<string, boolean | undefined> = {
      [workflowKey]: this.preferences[workflowKey]?.[channel],
    };
    if (this.preferences[workflowKey]) {
      this.preferences[workflowKey][channel] = enabled;
    }
    if (xorPartner) {
      previousValues[xorPartner] = this.preferences[xorPartner]?.[channel];
      if (this.preferences[xorPartner]) {
        this.preferences[xorPartner][channel] = false;
      }
    }

    try {
      this.isSaving = true;
      const client = getKnockClient();

      await client.user.setPreferences(this.buildPreferencesPayload());

      this.store.notification.enqueueSuccessSnackbar('Notification preference updated');
    } catch (e) {
      const role = this.store.session.user?.role;
      runInAction(() => {
        for (const [key, value] of Object.entries(previousValues)) {
          if (this.preferences[key]) {
            this.preferences[key][channel] = value ?? getWorkflowDefault(key, role);
          }
        }
      });
      this.store.notification.enqueueErrorSnackbar('Failed to update notification preference');
      Logger.error('Failed to update Knock preference', e);
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  }

  @action.bound
  reset(): void {
    this.isLoading = false;
    this.isLoaded = false;
    this.isFailure = false;
    this.isDisabled = false;
    this.isSaving = false;
    this.preferences = {};
    this.unknownWorkflows = {};
    this.unknownCategories = {};
  }
}

export default KnockPreferencesStore;
